import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Mail, Lock, Save, Plus, Trash2, Users, 
  Eye, EyeOff, Shield, Settings, RefreshCw, Clock, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewAdminForm, setShowNewAdminForm] = useState(false);
  
  // Booking settings
  const [bookingSettings, setBookingSettings] = useState({
    minimum_gap_hours: 4
  });
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);
  
  // Profile form
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // New admin form
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'admin'
  });

  const headers = {
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get current admin profile
      const profileRes = await axios.get(`${API_URL}/api/admin/profile`, { headers });
      setCurrentAdmin(profileRes.data);
      setProfile({
        username: profileRes.data.username || '',
        email: profileRes.data.email || '',
        first_name: profileRes.data.first_name || '',
        last_name: profileRes.data.last_name || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      // Get booking settings
      try {
        const settingsRes = await axios.get(`${API_URL}/api/admin/settings/booking`, { headers });
        setBookingSettings({
          minimum_gap_hours: settingsRes.data.minimum_gap_hours || 4
        });
      } catch (e) {
        console.error('Error fetching booking settings:', e);
      }
      
      // Get all admins (if super admin)
      try {
        const adminsRes = await axios.get(`${API_URL}/api/admin/users`, { headers });
        setAdmins(adminsRes.data);
      } catch (e) {
        // User might not have permission to list admins
        setAdmins([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSettingsUpdate = async (e) => {
    e.preventDefault();
    
    if (bookingSettings.minimum_gap_hours < 0 || bookingSettings.minimum_gap_hours > 48) {
      toast.error('Le délai doit être entre 0 et 48 heures');
      return;
    }
    
    setSavingBookingSettings(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/settings/booking?minimum_gap_hours=${bookingSettings.minimum_gap_hours}`, 
        {}, 
        { headers }
      );
      toast.success('Paramètres de réservation mis à jour');
    } catch (error) {
      console.error('Error updating booking settings:', error);
      toast.error(error.response?.data?.detail || 'Échec de la mise à jour');
    } finally {
      setSavingBookingSettings(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    // Validate password change
    if (profile.new_password) {
      if (!profile.current_password) {
        toast.error('Current password required to change password');
        return;
      }
      if (profile.new_password !== profile.confirm_password) {
        toast.error('New passwords do not match');
        return;
      }
      if (profile.new_password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }
    
    setSaving(true);
    try {
      const updateData = {
        email: profile.email,
        first_name: profile.first_name,
        last_name: profile.last_name
      };
      
      // Only include password change if requested
      if (profile.new_password) {
        updateData.current_password = profile.current_password;
        updateData.new_password = profile.new_password;
      }
      
      await axios.put(`${API_URL}/api/admin/profile`, updateData, { headers });
      
      toast.success('Profile updated successfully');
      setProfile(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      fetchData();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdmin.username || !newAdmin.password) {
      toast.error('Username and password are required');
      return;
    }
    
    if (newAdmin.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, newAdmin, { headers });
      toast.success('Admin created successfully');
      setShowNewAdminForm(false);
      setNewAdmin({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'admin'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${adminId}`, { headers });
      toast.success('Admin deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-[#181B1F]">Paramètres</h1>
          <p className="text-gray-500">Gérez votre profil, les utilisateurs admin et les paramètres de réservation</p>
        </div>
      </div>

      {/* Booking Settings Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Calendar className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-[#181B1F]">Paramètres de réservation</h2>
        </div>
        
        <form onSubmit={handleBookingSettingsUpdate} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-700 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Délai minimum entre deux événements (heures)
            </Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min="0"
                max="48"
                value={bookingSettings.minimum_gap_hours}
                onChange={(e) => setBookingSettings(prev => ({ 
                  ...prev, 
                  minimum_gap_hours: parseInt(e.target.value) || 0 
                }))}
                className="input-default w-32"
              />
              <span className="text-gray-500 text-sm">heures</span>
            </div>
            <p className="text-xs text-gray-400">
              Ce délai est nécessaire entre deux réservations ou blocages pour préparer et nettoyer le véhicule.
              Par exemple, si une réservation se termine à 14h00, la prochaine ne pourra commencer qu'à {14 + bookingSettings.minimum_gap_hours}h00.
            </p>
          </div>
          
          <Button
            type="submit"
            disabled={savingBookingSettings}
            className="btn-primary flex items-center gap-2"
          >
            {savingBookingSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </Button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <User className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-[#181B1F]">My Profile</h2>
          </div>
          
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">First Name *</Label>
                <Input
                  value={profile.first_name}
                  onChange={(e) => setProfile(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Last Name *</Label>
                <Input
                  value={profile.last_name}
                  onChange={(e) => setProfile(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Doe"
                  className="input-default"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Username</Label>
              <Input
                value={profile.username}
                disabled
                className="input-default bg-gray-100"
              />
              <p className="text-xs text-gray-400">Username cannot be changed</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@example.com"
                  className="input-default pl-10"
                />
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Change Password
              </h3>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-gray-600 text-sm">Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={profile.current_password}
                      onChange={(e) => setProfile(prev => ({ ...prev, current_password: e.target.value }))}
                      placeholder="••••••••"
                      className="input-default pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-600 text-sm">New Password</Label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={profile.new_password}
                      onChange={(e) => setProfile(prev => ({ ...prev, new_password: e.target.value }))}
                      placeholder="••••••••"
                      className="input-default"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600 text-sm">Confirm Password</Label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={profile.confirm_password}
                      onChange={(e) => setProfile(prev => ({ ...prev, confirm_password: e.target.value }))}
                      placeholder="••••••••"
                      className="input-default"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={saving}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
          </form>
        </div>

        {/* Admin Users Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-[#181B1F]">Admin Users</h2>
            </div>
            <Button
              onClick={() => setShowNewAdminForm(!showNewAdminForm)}
              className="btn-primary text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Admin
            </Button>
          </div>

          {/* New Admin Form */}
          {showNewAdminForm && (
            <form onSubmit={handleCreateAdmin} className="bg-gray-50 rounded-lg p-4 mb-6 space-y-4">
              <h3 className="font-medium text-[#181B1F]">New Admin</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-gray-600 text-sm">First Name *</Label>
                  <Input
                    value={newAdmin.first_name}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="John"
                    className="input-default"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-600 text-sm">Last Name *</Label>
                  <Input
                    value={newAdmin.last_name}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Doe"
                    className="input-default"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-gray-600 text-sm">Username *</Label>
                <Input
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="johndoe"
                  className="input-default"
                  required
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-gray-600 text-sm">Email</Label>
                <Input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  className="input-default"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-gray-600 text-sm">Password *</Label>
                <Input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className="input-default"
                  required
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex-1"
                >
                  {saving ? 'Creating...' : 'Create Admin'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowNewAdminForm(false)}
                  className="btn-outline"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Admin List */}
          <div className="space-y-3">
            {admins.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No other admins found</p>
            ) : (
              admins.map((admin) => (
                <div 
                  key={admin.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    admin.id === currentAdmin?.id 
                      ? 'bg-primary/5 border-primary/20' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#181B1F]">
                          {admin.first_name && admin.last_name 
                            ? `${admin.first_name} ${admin.last_name}` 
                            : admin.username}
                        </span>
                        {admin.id === currentAdmin?.id && (
                          <Badge className="bg-primary/10 text-primary text-xs">You</Badge>
                        )}
                        <Badge className="bg-gray-200 text-gray-600 text-xs">{admin.role}</Badge>
                      </div>
                      <p className="text-sm text-gray-500">@{admin.username}</p>
                      {admin.email && (
                        <p className="text-xs text-gray-400">{admin.email}</p>
                      )}
                    </div>
                  </div>
                  
                  {admin.id !== currentAdmin?.id && (
                    <Button
                      onClick={() => handleDeleteAdmin(admin.id)}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-lg"
                      variant="ghost"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              <Shield className="w-4 h-4 inline mr-1" />
              <strong>Note:</strong> The admin's first name and last name will appear as the "Company Representative" on delivery and return reports (PV).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
